/**
 * API client for the Function App's youtube/* read endpoints,
 * proxied through SWA's same-origin /api/* (the wildcard managed function
 * injects the function key server-side).
 *
 * Response shapes mirror function-app/sources/youtube.py _shape_video etc.
 */

import { checkUnauthorized } from "./auth";

export interface Video {
  videoId: string;
  title: string;
  publishedAt: string;
  durationSeconds: number;
  isLivestream: boolean;
  transcriptStatus: "complete" | "pending" | "failed" | "skipped" | "";
  lastCommentFetchAt: string;
  lastDigestAt: string;
  newCommentsSinceLastDigest: number;
  digestThreshold: number;
  commentFetchBackoffTier: 0 | 1 | 2 | 3;
  consecutiveEmptyFetches: number;
}

export interface DigestVersion {
  version: string;
  generatedAt: string;
  path: string;
  sizeBytes: number;
}

export interface DigestContent {
  videoId: string;
  version: string;
  markdown: string;
}

export interface MetricPoint {
  fetchedAt: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  favouriteCount: number;
}

/** v8 enum vocabularies (mirror function-app/sources/comment_tags.py). */
export type Sentiment = "supportive" | "unsupportive" | "neutral";
export const SENTIMENTS: Sentiment[] = ["supportive", "unsupportive", "neutral"];
export type GeneralTag =
  | "question"
  | "challenge"
  | "misunderstanding"
  | "personal_story"
  | "content_request";
export const GENERAL_TAGS: GeneralTag[] = [
  "question",
  "challenge",
  "misunderstanding",
  "personal_story",
  "content_request",
];
export type SafetyTag = "spam_bot" | "threat_or_abuse";
export const SAFETY_TAGS: SafetyTag[] = ["spam_bot", "threat_or_abuse"];

/** A topic_tag resolved against the `themes` picklist (label may be the id if retired). */
export interface TopicLabel {
  id: string;
  label: string;
}

export interface Comment {
  commentThreadId: string;
  videoId: string;
  /** Commenter identity: served only to safety reviewers, on flagged rows (GDPR #27/#29). */
  author?: string;
  authorChannelId?: string;
  text: string;
  likeCount: number;
  replyCount: number;
  /** Computed server-side from like/reply counts — not a classifier category. */
  highEngagement: boolean;
  publishedAt: string;
  // v8 multi-tag dimensions
  sentiment: Sentiment | "";
  generalTags: string[];
  topicTags: string[];
  topicLabels: TopicLabel[];
  safetyTags: string[];
  safetyNotes: string;
  classifiedAt: string;
}

export interface VideoCommentStats {
  videoId: string;
  total: number;
  bySentiment: Record<string, number>;
  byGeneralTag: Record<string, number>;
  byTopicTag: Record<string, number>;
  /** topic_tag id → human label (from the themes picklist). */
  topicLabels: Record<string, string>;
}

export interface ListCommentsParams {
  // v8 facets — AND-composed. sentiment filters server-side; the three tag
  // params are Python post-filtered (see list_comments in youtube.py).
  sentiment?: Sentiment;
  generalTag?: GeneralTag;
  topicTag?: string;
  safetyTag?: SafetyTag;
  videoId?: string;
  /** Computed filter: likeCount > threshold OR replyCount > threshold (server-side). */
  highEngagement?: boolean;
  pageSize?: number;
  continuation?: string;
}

/** Thrown by api.chat on a 429 daily_limit; the message is user-facing as is. */
export class ChatLimitError extends Error {}

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

/**
 * The chat context envelope (Phase 7d). Every UI chat surface maps to one of
 * these; the broker folds it into a scoping note so the agent grounds its tool
 * calls on what the user is currently looking at.
 */
export type ChatContext =
  | { type: "channel" }
  | { type: "video"; videoId: string }
  | {
      type: "comments";
      filters: {
        sentiment?: Sentiment;
        generalTag?: GeneralTag;
        topicTag?: string;
        safetyTag?: SafetyTag;
        videoId?: string;
      };
    };

/**
 * Overview blob shape — built by compute_overview() in
 * function-app/sources/youtube.py. Snake_case keys preserved verbatim
 * from the source (it's the raw blob, not _shape_*-shaped).
 */
export interface OverviewData {
  generated_at: string;
  videos: {
    total: number;
    livestreams: number;
    by_transcript_status: Record<string, number>;
    by_backoff_tier: Record<string, number>;
    with_digest: number;
    never_fetched: number;
  };
  comments: {
    total: number;
    classified: number;
    classified_pct: number;
    // v8 dimensions
    by_sentiment: Record<string, number>;
    by_general_tag: Record<string, number>;
    by_topic_tag: Record<string, number>;
    /** topic_tag id → human label (from the themes picklist). */
    topic_labels: Record<string, string>;
  };
  engagement: {
    videos_with_metrics: number;
    total_views: number;
    total_likes: number;
    total_reported_comments: number;
  };
  /** Per-video latest counts (compact keys), keyed by videoId. Powers the
   * portal cross-signal chart's per-upload reach bars. Absent on overview blobs
   * written before this field shipped. */
  video_stats?: Record<string, { v: number; l: number; c: number }>;
  pipeline: {
    last_comment_fetch_at: string;
    last_digest_at: string;
  };
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  checkUnauthorized(res);
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} — ${path}`);
  }
  return (await res.json()) as T;
}

/**
 * Phase 8 — themes management. Mirrors function-app/sources/themes_admin.py.
 * Status is one of "active" | "suggested" | "disabled"; the portal enforces
 * the 10-active hard limit by surfacing 409 errors from the server (the API
 * itself is the source of truth — UI just renders the error inline).
 */
export type ThemeStatus = "active" | "suggested" | "disabled";
export type ThemeSource = "manual" | "cluster" | "eval_gold";

export interface ManagedTheme {
  theme_id: string;
  label: string;
  description: string;
  status: ThemeStatus;
  source: ThemeSource;
  source_category: string;
  seed_origin: string;
  cluster_size: number;
  sweep_k_override: number | null;
  created_at: string;
  last_modified_at: string;
}

export interface ThemesListResponse {
  themes: ManagedTheme[];
  active_count: number | null;
  active_limit: number;
}

export interface ThemeMutationError {
  status: number;
  message: string;
}

/**
 * Admin user management (portal app, admin-only; the proxy also enforces it).
 * Failures throw an Error whose message is the backend `error` code
 * (exists, invalid_email, self_lockout, last_admin, forbidden, not_found, �).
 */
export type PortalRole = "member" | "admin" | "safety_reviewer";

export interface PortalUserRow {
  email: string;
  name: string;
  roles: string[];
  status: "active" | "disabled";
  /** Has signed in before (linked to an Andian sign-in identity). */
  bound: boolean;
  lastSignInAt: string;
  createdAt: string;
  activeSessions: number;
}

export interface PortalUsersResponse {
  users: PortalUserRow[];
  assignableRoles: string[];
}

async function mutateJson<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: { Accept: "application/json" },
  };
  if (body !== undefined) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  checkUnauthorized(res);
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON response */
  }
  if (!res.ok) {
    const detail =
      parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `${res.status} ${res.statusText}`;
    const err = new Error(detail) as Error & ThemeMutationError;
    err.status = res.status;
    err.message = detail;
    throw err;
  }
  return parsed as T;
}

/**
 * fncapp wraps array responses in envelopes:
 *   GET /youtube/videos              → { items: Video[] }
 *   GET /youtube/videos/{id}/digests → { videoId, versions: DigestVersion[] }
 *   GET /youtube/videos/{id}/metrics → { videoId, items: MetricPoint[] }
 *   GET /youtube/videos/{id}/comments → { videoId, items: Comment[], continuation }
 * The client unwraps these so callers always get bare arrays (or paged objects).
 */

export const api = {
  videos: async () =>
    (await fetchJson<{ items: Video[] }>("/api/youtube/videos")).items,
  video: (id: string) => fetchJson<Video>(`/api/youtube/videos/${id}`),
  digestVersions: async (id: string) =>
    (
      await fetchJson<{ videoId: string; versions: DigestVersion[] }>(
        `/api/youtube/videos/${id}/digests`,
      )
    ).versions,
  digest: (id: string, version = "latest") =>
    fetchJson<DigestContent>(
      `/api/youtube/videos/${id}/digests/${version}`,
    ),
  metrics: async (id: string) =>
    (
      await fetchJson<{ videoId: string; items: MetricPoint[] }>(
        `/api/youtube/videos/${id}/metrics`,
      )
    ).items,
  comments: (id: string, opts?: { pageSize?: number }) => {
    const qs = new URLSearchParams();
    if (opts?.pageSize) qs.set("page_size", String(opts.pageSize));
    const q = qs.toString();
    return fetchJson<{
      videoId: string;
      items: Comment[];
      continuation?: string;
    }>(`/api/youtube/videos/${id}/comments${q ? `?${q}` : ""}`);
  },
  overview: () => fetchJson<OverviewData>("/api/youtube/overview"),
  commentStats: (id: string) =>
    fetchJson<VideoCommentStats>(`/api/youtube/videos/${id}/comment-stats`),
  listComments: (params: ListCommentsParams = {}) => {
    const qs = new URLSearchParams();
    // v8 facets
    if (params.sentiment) qs.set("sentiment", params.sentiment);
    if (params.generalTag) qs.set("general_tag", params.generalTag);
    if (params.topicTag) qs.set("topic_tag", params.topicTag);
    if (params.safetyTag) qs.set("safety_tag", params.safetyTag);
    if (params.videoId) qs.set("videoId", params.videoId);
    if (params.highEngagement) qs.set("high_engagement", "1");
    if (params.pageSize) qs.set("page_size", String(params.pageSize));
    if (params.continuation) qs.set("continuation", params.continuation);
    const q = qs.toString();
    return fetchJson<{
      filters: {
        sentiment: string;
        generalTag: string;
        topicTag: string;
        safetyTag: string;
        videoId: string;
        highEngagement: boolean;
      };
      items: Comment[];
      continuation: string | null;
    }>(`/api/youtube/comments${q ? `?${q}` : ""}`);
  },
  themesManage: {
    list: (status?: ThemeStatus) => {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      return fetchJson<ThemesListResponse>(`/api/youtube/themes/manage${q}`);
    },
    get: (themeId: string) =>
      fetchJson<{ theme: ManagedTheme }>(
        `/api/youtube/themes/manage/${encodeURIComponent(themeId)}`,
      ),
    create: (body: {
      theme_id: string;
      label: string;
      description?: string;
      status?: ThemeStatus;
      source?: ThemeSource;
    }) =>
      mutateJson<{ theme: ManagedTheme }>(
        "/api/youtube/themes/manage",
        "POST",
        body,
      ),
    patch: (
      themeId: string,
      body: Partial<{
        label: string;
        description: string;
        status: ThemeStatus;
        sweep_k_override: number | null;
      }>,
    ) =>
      mutateJson<{ theme: ManagedTheme }>(
        `/api/youtube/themes/manage/${encodeURIComponent(themeId)}`,
        "PATCH",
        body,
      ),
  },
  settings: {
    listUsers: () => fetchJson<PortalUsersResponse>("/api/settings/users"),
    createUser: (body: { email: string; name: string; roles: string[] }) =>
      mutateJson<{ user: PortalUserRow }>("/api/settings/users", "POST", body),
    updateUser: (
      body: { email: string } & Partial<{
        name: string;
        roles: string[];
        status: "active" | "disabled";
      }>,
    ) => mutateJson<{ user: PortalUserRow }>("/api/settings/users", "PATCH", body),
    removeUser: (email: string) =>
      mutateJson<{ removed: boolean }>("/api/settings/users/remove", "POST", {
        email,
      }),
  },
  /**
   * Conversation state is server-side: the broker returns a `conversationId`
   * the caller holds and re-sends each turn (no client-side history replay).
   */
  chat: async (body: {
    message: string;
    conversationId?: string;
    context: ChatContext;
  }): Promise<{ reply: string; conversationId?: string }> => {
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message: body.message,
        conversation_id: body.conversationId,
        context: body.context,
      }),
    });
    checkUnauthorized(res);
    if (res.status === 429) {
      const err = (await res.json().catch(() => ({}))) as { error?: string; limit?: number };
      if (err.error === "daily_limit") {
        throw new ChatLimitError(
          `You've reached today's chat limit${
            err.limit ? ` (${err.limit} messages)` : ""
          }. It resets at midnight UTC.`,
        );
      }
    }
    if (!res.ok) {
      throw new Error(`${res.status} ${res.statusText}`);
    }
    const data = (await res.json()) as {
      reply?: string;
      conversation_id?: string;
    };
    return { reply: data.reply ?? "", conversationId: data.conversation_id };
  },
};

/* ----------------------------------------------------------------------------
 * Agent 2 — Salience monitor. Routed through the same /api/* proxy to the
 * salience backend (proxy.js sends /api/salience/* to SALIENCE_BASE_URL).
 * S2b: the coder-reliability gold-set marking surface.
 * -------------------------------------------------------------------------- */

export type SalienceRelevant = "" | "yes" | "no";
export const SALIENCE_FRAMES = [
  "fairness",
  "capital_flight",
  "feasibility",
  "billionaires",
  "none",
] as const;
export const SALIENCE_STANCES = [
  "supportive",
  "opposed",
  "neutral",
  "mixed",
] as const;
export const SALIENCE_TONES = ["positive", "negative", "neutral"] as const;

export interface SalienceEvalItem {
  id: string;
  source: string;
  term_id: string;
  title: string;
  text: string;
  matched_phrase: string;
  url: string;
  gold_relevant: SalienceRelevant;
  gold_frame: string;
  gold_stance: string;
  gold_tone: string;
  gold_entity_mentions: string[];
  gold_tagline_matched: boolean;
  gold_notes: string;
  marked: boolean;
  marked_at: string;
}

export interface SalienceEvalProgress {
  eval_set: string;
  total: number;
  marked: number;
  remaining: number;
}

/** The mark payload the marking page POSTs. */
export interface SalienceMark {
  relevant: "yes" | "no";
  frame?: string;
  stance?: string;
  tone?: string;
  entity_mentions?: string[];
  tagline_matched?: boolean;
  notes?: string;
}

export interface SalienceTerm {
  term_id: string;
  kind: "topic" | "tagline" | "entity";
  label: string;
  query_variants: string[];
  exact_phrases: string[];
  status: string;
  topic_scoped: boolean;
  notes?: string;
}

/* ----------------------------------------------------------------------------
 * S5 read surface — the salience monitor dashboard. Shapes mirror
 * salience-app/sources/reads.py (_shape_metric / _shape_spike / _shape_influence
 * / _shape_calendar) — camelCase, JSON fields already parsed to objects.
 *
 * Honest construct labelling (plan §1.1): these series measure ATTENTION /
 * agenda-prominence, never "importance". Three agendas (public / media / elite)
 * are kept strictly separate, never blended into one number.
 * -------------------------------------------------------------------------- */

export type SalienceAgenda = "public" | "media" | "elite" | "search";
export const SALIENCE_AGENDAS: SalienceAgenda[] = ["public", "media", "elite", "search"];

export const SALIENCE_AGENDA_LABELS: Record<string, string> = {
  public: "Public",
  media: "Media",
  elite: "Elite",
  search: "Search",
};

/** What each agenda's headline metric actually is — drives axis + copy. */
export const SALIENCE_AGENDA_BLURB: Record<string, string> = {
  public: "YouTube topic volume — count of relevant videos per day.",
  media: "GDELT coverage — % share of global media coverage.",
  elite: "UK Parliament — count of relevant items per day.",
  search: "Google Trends — relative search interest, 0–100 (100 = the term's peak week; not a %). Monthly before ~2022, weekly after.",
};

export interface SalienceMetricPoint {
  termId: string;
  agenda: string;
  geo: string;
  bucketDate: string;
  volume: number | null;
  /** Fraction (0–1). ×100 for a percentage. shareLabel says what it's a share of. */
  share: number | null;
  shareLabel: string;
  velocity: number | null;
  stanceSplit: Record<string, number>;
  frameSplit: Record<string, number>;
  toneMean: number | null;
  toneScale: string;
  codedItems: number;
  garyVideoCount: number | null;
  computedAt: string;
}

export interface GaryUpload {
  video_id: string;
  published_at: string;
  title: string;
}

export interface CalendarHit {
  event_date: string;
  event_type: string;
  label: string;
}

export interface SalienceSpike {
  kind: "spike";
  termId: string;
  agenda: string;
  geo: string;
  bucketDate: string;
  metric: string;
  value: number | null;
  baselineMean: number | null;
  baselineStd: number | null;
  zScore: number | null;
  ratio: number | null;
  thresholdZ: number | null;
  baselineDays: number | null;
  calendarHits: CalendarHit[];
  calendarHitCount: number;
  nearExogenous: boolean;
  garyUploads: GaryUpload[];
  garyUploadCount: number;
  nearGaryUpload: boolean;
  detectedAt: string;
  sortDate: string;
}

export interface SalienceInfluence {
  kind: "influence";
  termId: string;
  leaderAgenda: string;
  followerAgenda: string;
  geo: string;
  bestLagDays: number | null;
  correlation: number | null;
  nOverlap: number | null;
  leaderActiveDays: number | null;
  followerActiveDays: number | null;
  grangerF: number | null;
  grangerP: number | null;
  dfNum: number | null;
  dfDen: number | null;
  candidate: boolean;
  underpowered: boolean;
  caveat: string;
  followerPeakDate: string;
  followerPeakValue: number | null;
  confounderCalendar: CalendarHit[];
  confounderCount: number;
  window: string;
  computedAt: string;
  sortDate: string;
}

export type SalienceEvent = SalienceSpike | SalienceInfluence;

export interface SalienceCalendarEvent {
  eventDate: string;
  eventType: string;
  label: string;
  description: string;
  sourceUrl: string;
  geo: string;
  institutions: string[];
}

/** A GDELT headline behind the media coverage — DOC ArtList forward capture
 * or BigQuery GKG spike-day capture (the latter has slug-derived titles). */
export interface SalienceArticle {
  termId: string;
  seenDate: string;
  title: string;
  url: string;
  domain: string;
  language: string;
  sourceCountry: string;
  socialImage: string;
  /** BQ spike-capture extras (absent on DOC ArtList rows). */
  syndicationCount?: number | null;
  titleSource?: string;
}

export interface SalienceArticlesParams {
  term?: string;
  from?: string;
  to?: string;
}

/** One YouTube video behind the public-agenda volume line (salienceObservations
 * provenance — the public counterpart to SalienceArticle). */
export interface SalienceObservation {
  termId: string;
  observedAt: string;
  /** Where the item came from: youtube | hansard | edm | wpq | petition | govuk. */
  source: string;
  title: string;
  url: string;
  videoId: string;
  channelTitle: string;
  channelId: string;
  /** MP / peer / petition creator behind a gov item (empty for videos). */
  memberName: string;
  matchedPhrase: string;
  stance: string;
  isGary: boolean;
}

export interface SalienceObservationsParams {
  term?: string;
  from?: string;
  to?: string;
  /** Comma list of sources — omit for youtube (public drill); the elite drill
   * passes the five gov sources. */
  sources?: string;
}

/** Per-geo headline snapshot inside the overview blob (snake_case — raw blob). */
export interface SalienceOverviewGeo {
  latest_date: string;
  latest_volume: number | null;
  latest_share: number | null;
  share_label: string;
  velocity: number | null;
  recent_7d_avg_share?: number;
  recent_7d_volume?: number;
}

export interface SalienceOverviewTerm {
  label: string;
  kind: string;
  agendas: Record<string, Record<string, SalienceOverviewGeo>>;
}

export interface SalienceOverview {
  computed_at: string;
  terms: Record<string, SalienceOverviewTerm>;
}

export interface SalienceTimeseriesParams {
  term?: string;
  agenda?: SalienceAgenda;
  geo?: string;
  from?: string;
  to?: string;
}

export interface SalienceEventsParams {
  term?: string;
  kind?: "spike" | "influence";
  agenda?: SalienceAgenda;
}

export interface SalienceCalendarParams {
  from?: string;
  to?: string;
  event_type?: string;
  geo?: string;
}

export const apiSalience = {
  overview: () => fetchJson<SalienceOverview>("/api/salience/overview"),
  terms: async (kind?: string) => {
    const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
    return (
      await fetchJson<{ terms: SalienceTerm[] }>(`/api/salience/terms${q}`)
    ).terms;
  },
  timeseries: async (params: SalienceTimeseriesParams = {}) => {
    const qs = new URLSearchParams();
    if (params.term) qs.set("term", params.term);
    if (params.agenda) qs.set("agenda", params.agenda);
    if (params.geo) qs.set("geo", params.geo);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const q = qs.toString();
    return (
      await fetchJson<{ items: SalienceMetricPoint[] }>(
        `/api/salience/timeseries${q ? `?${q}` : ""}`,
      )
    ).items;
  },
  events: async (params: SalienceEventsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.term) qs.set("term", params.term);
    if (params.kind) qs.set("kind", params.kind);
    if (params.agenda) qs.set("agenda", params.agenda);
    const q = qs.toString();
    return (
      await fetchJson<{ items: SalienceEvent[] }>(
        `/api/salience/events${q ? `?${q}` : ""}`,
      )
    ).items;
  },
  geo: async (term: string, agenda?: SalienceAgenda) => {
    const qs = new URLSearchParams({ term });
    if (agenda) qs.set("agenda", agenda);
    return (
      await fetchJson<{ items: SalienceMetricPoint[] }>(
        `/api/salience/geo?${qs.toString()}`,
      )
    ).items;
  },
  calendar: async (params: SalienceCalendarParams = {}) => {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.event_type) qs.set("event_type", params.event_type);
    if (params.geo) qs.set("geo", params.geo);
    const q = qs.toString();
    return (
      await fetchJson<{ items: SalienceCalendarEvent[] }>(
        `/api/salience/calendar${q ? `?${q}` : ""}`,
      )
    ).items;
  },
  articles: async (params: SalienceArticlesParams = {}) => {
    const qs = new URLSearchParams();
    if (params.term) qs.set("term", params.term);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const q = qs.toString();
    return (
      await fetchJson<{ items: SalienceArticle[] }>(
        `/api/salience/articles${q ? `?${q}` : ""}`,
      )
    ).items;
  },
  /** Per-video provenance behind the public (YouTube) volume line — the public
   * counterpart to `articles`. Relevant-coded videos only (reconciles with the
   * chart's count). Already in salienceObservations, so no on-demand capture. */
  observations: async (params: SalienceObservationsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.term) qs.set("term", params.term);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.sources) qs.set("sources", params.sources);
    const q = qs.toString();
    return (
      await fetchJson<{ items: SalienceObservation[] }>(
        `/api/salience/observations${q ? `?${q}` : ""}`,
      )
    ).items;
  },
  /** On-demand one-day coverage lookup — cached salienceArticles when the day
   * was already captured, otherwise a live one-day BigQuery GKG capture
   * (so the first click on an uncaptured day takes a few seconds). */
  articlesLookup: async (term: string, day: string) =>
    (
      await fetchJson<{ items: SalienceArticle[]; source: string }>(
        `/api/salience/articles/lookup?term=${encodeURIComponent(term)}&day=${encodeURIComponent(day)}`,
      )
    ).items,
  eval: {
    items: () =>
      fetchJson<{
        eval_set: string;
        items: SalienceEvalItem[];
        progress: SalienceEvalProgress;
      }>("/api/salience/eval/items"),
    save: (id: string, mark: SalienceMark) =>
      mutateJson<{ item: SalienceEvalItem }>(
        `/api/salience/eval/items/${encodeURIComponent(id)}`,
        "POST",
        mark,
      ),
    progress: () =>
      fetchJson<SalienceEvalProgress>("/api/salience/eval/progress"),
  },
  /** Active entity terms — the closed picklist the marker tags entity mentions from. */
  entities: async () =>
    (
      await fetchJson<{ terms: SalienceTerm[] }>(
        "/api/salience/terms/manage?kind=entity&status=active",
      )
    ).terms,
};

/**
 * Collect up to `target` matching comments, transparently following the
 * server's bounded-scan continuations.
 *
 * The /comments endpoint post-filters the tag facets (general/topic/safety)
 * over a bounded row window per call, so a selective facet can return a short
 * — occasionally empty — page plus a continuation token. Surfacing that to the
 * user as "keep scanning" was confusing; instead we loop here until we have a
 * screenful of matches, the scan is exhausted (continuation === null), or we
 * hit `maxCalls` (a safety bound against a pathological scan). Callers show a
 * single loading state for the whole collect, then render the matches.
 *
 * Returns the accumulated items plus the continuation to resume from for the
 * next page (null once the scan is exhausted).
 */
export async function collectComments(
  params: ListCommentsParams,
  target: number,
  startContinuation?: string,
  maxCalls = 40,
): Promise<{ items: Comment[]; continuation: string | null }> {
  const items: Comment[] = [];
  let continuation: string | undefined = startContinuation;
  let calls = 0;
  do {
    const res = await api.listComments({
      ...params,
      pageSize: target,
      continuation,
    });
    items.push(...res.items);
    continuation = res.continuation ?? undefined;
    calls += 1;
  } while (continuation && items.length < target && calls < maxCalls);
  return { items, continuation: continuation ?? null };
}
